import { useDetectScrollEnd } from "components/hooks";
import { HeaderHeight, MncVars, NavigatorHeight } from "const";
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import classNames from "classnames";
import PDF from ".";
import { MenuSelector } from "redux/selector/menu-selector";
import { useSelector } from "react-redux";
import useFallcackCalc from "components/hooks/useFallcackCalc";
import { execNumber, getPropertyValue } from "utils/misc/css-util";

const defaultDetectionConfig = { once: true, enabledDetection: false };

function ForceReadPDF({
  className,
  style,
  pdf,
  children,
  onPagesLoaded,
  onDocumentLoaded,
  onReadCompleted,
  avoidTemplate,
  adjustPdfViewSize,
  renderBottom,
  pdfProps,
  detectScrollEndConfig,
  ...props
}) {
  const isManualMode = children != null;
  const [readCompleted, setReadCompleted] = useState(false);
  const [pdfRendered, setPdfRendered] = useState(false);
  const [scrollbarRef, isEnd, , enabled] = useDetectScrollEnd({ ...defaultDetectionConfig, ...detectScrollEndConfig });
  const forceHideAllMenu = useSelector(MenuSelector.forceHideAllMenu);

  const headerHeight = execNumber(getPropertyValue({ varName: MncVars.Header, fallback: HeaderHeight }));
  const navbarHeight = execNumber(getPropertyValue({ varName: MncVars.Navbar, fallback: NavigatorHeight }));
  const adjust = (avoidTemplate ? -((forceHideAllMenu ? 0 : navbarHeight) + headerHeight) : 0) + adjustPdfViewSize ?? 0;

  useFallcackCalc(".force-read-pdf", "--force-pdf-container-height", 100, {
    adjust,
  });

  useEffect(() => {
    if (isManualMode) {
      onDocumentLoaded?.();
      onPagesLoaded?.();
      enabled();
      setPdfRendered(true);
    }
  }, [isManualMode, onDocumentLoaded, onPagesLoaded, enabled]);

  useEffect(() => {
    if (!readCompleted && isEnd && pdfRendered && (pdf || isManualMode)) {
      setReadCompleted(true);
      onReadCompleted?.();
    }
  }, [isEnd, readCompleted, pdf, isManualMode, pdfRendered, onReadCompleted]);

  const _onPagesLoaded = useCallback(() => {
    enabled();
    setPdfRendered(true);

    if (onPagesLoaded) {
      onPagesLoaded();
    }
  }, [enabled, onPagesLoaded]);

  const _renderBottom = useCallback(() => {
    return renderBottom && renderBottom(readCompleted);
  }, [readCompleted, renderBottom]);

  return (
    <PdfContainer
      ref={scrollbarRef}
      className={classNames("force-read-pdf", className)}
      style={style}
      $adjust={adjust}
      {...props}
    >
      {isManualMode ? (
        <>
          {children}
          {renderBottom && (typeof renderBottom === "function" ? renderBottom(readCompleted) : renderBottom)}
        </>
      ) : (
        <PDF
          file={pdf}
          onDocumentLoaded={onDocumentLoaded}
          onPagesLoaded={_onPagesLoaded}
          renderBottom={_renderBottom}
          {...(pdfProps || {})}
        />
      )}
    </PdfContainer>
  );
}

export default ForceReadPDF;

ForceReadPDF.defaultProps = {
  adjustPdfViewSize: 0,
  avoidTemplate: true,
};

const PdfContainer = styled.div`
  overflow-y: auto;
  overscroll-behavior-y: none;
  border-radius: 5px;

  height: var(--force-pdf-container-height);
  height: calc(var(--app-height) + ${(props) => props.$adjust ?? 0}px);
  height: calc(100vh + ${(props) => props.$adjust ?? 0}px);
  height: calc(100dvh + ${(props) => props.$adjust ?? 0}px);
`;
